import {
  createSiteTemplate,
  deleteSiteTemplate,
} from '@lms/api-client/backend';
import { z } from 'zod';

// Ports app/[brand]/admin/templates/_components/actions.schema.ts
export const addSiteTemplateSchema = z.object({
  name: z.preprocess((v) => (v === '' ? undefined : v), z.string().min(1)),
  site_id: z.preprocess(
    (v) =>
      v === '' || v === undefined ? undefined : Number.parseInt(`${v}`, 10),
    z.number().gte(1),
  ),
  description: z.string().optional(),
});

export interface AddSiteTemplateInput {
  name: string;
  site_id: string;
  description?: string;
}

export type AddSiteTemplateResult =
  | { status: 'success' }
  | {
      status: 'error';
      fieldErrors?: {
        name?: string;
        site_id?: string;
        description?: string;
      };
      formErrors?: string[];
    };

/**
 * Create a site-template from an existing site. Ports `addSiteTemplate` from the
 * Next app's _components/actions.ts: the server action validated with conform +
 * zod, revalidated the list path and `redirect`ed on success. Here it validates
 * inline and returns a result the caller acts on (navigate + SWR mutate).
 */
export async function addSiteTemplate(
  input: AddSiteTemplateInput,
): Promise<AddSiteTemplateResult> {
  const submission = addSiteTemplateSchema.safeParse({
    name: input.name,
    site_id: input.site_id,
    description: input.description,
  });

  if (!submission.success) {
    const fieldErrors = submission.error.flatten().fieldErrors;
    return {
      status: 'error',
      fieldErrors: {
        name: fieldErrors.name?.[0],
        site_id: fieldErrors.site_id?.[0],
        description: fieldErrors.description?.[0],
      },
    };
  }

  const { name, site_id, description } = submission.data;

  try {
    await createSiteTemplate({
      name,
      siteId: site_id,
      description: description ?? undefined,
    });
  } catch (e) {
    return {
      status: 'error',
      formErrors: [`Failed to create template: ${e}`],
    };
  }

  return { status: 'success' };
}

/**
 * Delete a site-template. Ports `removeSiteTemplate` from _components/actions.ts;
 * the caller revalidates the list via SWR mutate (the Next action used
 * `revalidatePath`).
 */
export async function removeSiteTemplate(id: number): Promise<void> {
  await deleteSiteTemplate(id);
}
